import { usePostHog } from 'posthog-js/react';
import { FC } from 'react';

import { ImagesApp } from '../../../constants/images';
import { useBreadCrumbsContext } from '../../../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../../../hooks/contexts/cart.context';
import { useEventContext } from '../../../hooks/contexts/event.context';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import { useModalContext } from '../../../hooks/contexts/modal.context';
import useGuardian from '../../../hooks/useGuardian';
import { computeCartTotals } from '../../../utils/cartTotals';
import formatNumericValue from '../../../utils/formatNumericValue';
import shouldDisableButton from '../../../utils/shouldDisableButton';
import { BigButton } from '../../Buttons';
import FeesBreakdown from './FeesBreakdown';
import NoTickets from './NoTickets';
import RestrictedAssign from './RestrictedAssign';
import SelectedTickets, { TicketCart } from './SelectedTickets';
import * as Styles from './summary.styles';

const Summary: FC = () => {
  const posthog = usePostHog();
  const { translate } = useI18nContext();
  const { currencyFormat, loading, event } = useEventContext();
  const {
    summaryData,
    cart,
    cartLoading,
    combosInCart,
    getTotalTicketsInCart,
    namingData,
  } = useCartContext();
  const { navigateToNextRoute } = useGuardian();
  const { isNamingPage } = useBreadCrumbsContext();
  const { toggleModal, setContentModal, defineTitleModal, showModal } = useModalContext();

  const { subtotal, comboDiscount, serviceFee, total } = computeCartTotals({ summaryData, combosInCart });

  const hasMinimumTickets = cart.map((item) =>
    item.type.map((ticket) => ticket.quantityInCart < ticket.restrictions.minimum)
  );

  const findMinimumTickets = hasMinimumTickets.findIndex((item, i) => item[i] === true);

  const transferIsRequired = event?.attributes.ticketTransferRequired.value;
  const hasMandatoryFaceRecognition = namingData.some((curData) => curData.transferMandatoryFaceRecognition);
  const hasOptionalFaceRecognitionAndNoTransferable = namingData.some((curData) => (
    curData.faceRecognition && !curData.isTransferableGuestType
  ));

  const isTheNamingPageAndIsOk = namingData.reduce((acc, item) => {
    return item.holder ? acc : acc + 1;
  }, 0) > 0 && isNamingPage;

  const verifyDisableBtnRule = (
    transferIsRequired ||
    hasMandatoryFaceRecognition ||
    hasOptionalFaceRecognitionAndNoTransferable
  );

  const disableBtnInNamingPage = verifyDisableBtnRule ? isTheNamingPageAndIsOk : false;
  const handleNextStep = () => {
    const isntTransferInWallet = !event?.attributes.ticketTransferEnabled.value;
    if (isNamingPage && isntTransferInWallet) {
      defineTitleModal(translate(['INFORMATION', 'RESTRICTED_ASSIGNMENT']));
      setContentModal(<RestrictedAssign navigateToNextRoute={navigateToNextRoute} />);
      toggleModal({ open : !showModal });
      return;
    }
    posthog?.capture('purchase:ticket_selection_continue_click');
    navigateToNextRoute();
  };

  const isDisabled = shouldDisableButton({
    isNamingPage,
    disableBtnInNamingPage,
    findMinimumTickets,
    cart,
    combosInCart,
    loading,
    cartLoading
  });

  return (
    <Styles.Container id="summary-container">
      <Styles.SummaryHeader id="summary-header">
        <img src={ImagesApp.svg.summary} alt="summary" id="summary-header-img" />
        <h1 id="summary-header-title">{translate(['STEPS', 'TXT13'])}</h1>
      </Styles.SummaryHeader>
      <Styles.Detailing id="summary-detailing">
        {
          summaryData.length === 0 && combosInCart.length === 0 ? (
            <NoTickets />
          ) : (
            <SelectedTickets
              tickets={summaryData as unknown as TicketCart[]}
              combosInCart={combosInCart}
            />
          )
        }
      </Styles.Detailing>
      <Styles.SummaryFooter id="summary-footer">
        <Styles.RowFullWidth $align='space-between' id="summary-row-total-tickets">
          <Styles.TextInfo
            $weight={400}
            size={12}
            id="summary-total-tickets-label"
          >
            {translate(['STEPS', 'TXT4'])}
          </Styles.TextInfo>
          <Styles.TextInfo
            $weight={700}
            size={12}
            id="summary-total-tickets-value"
          >
            {getTotalTicketsInCart()}
          </Styles.TextInfo>
        </Styles.RowFullWidth>
        {combosInCart.length > 0 && (
          <Styles.RowFullWidth $align='space-between' id="combo-benefit">
            <Styles.TextInfo $weight={400} size={12} id="summary-combo-benefit">
              {translate(['COMBO', 'SUMMARY', 'TOTAL_DISCOUNT_MESSAGE'])}
            </Styles.TextInfo>
            <Styles.TextInfo $weight={700} size={12} id="summary-total-combo-discount">
              - {formatNumericValue({
                value : comboDiscount,
                discount : 0,
                currencyFormat
              })}
            </Styles.TextInfo>
          </Styles.RowFullWidth>
        )}
        <Styles.RowFullWidth $align='space-between' id="summary-row-total-value">
          <Styles.TextInfo $weight={400} size={12} id="summary-total-value-label">
            {translate(['STEPS', 'TXT14'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={12} id="summary-total-value-amount">
            {formatNumericValue({
              value : subtotal,
              discount : 0,
              currencyFormat
            })}
          </Styles.TextInfo>
        </Styles.RowFullWidth>
        {/* Only a cart that carries a service fee shows the block: with none (free order, or a producer
           absorbing it) a "Taxas R$ 0,00" line just alarms the buyer (product decision, MKT-1043). */}
        {serviceFee > 0 && (
          <Styles.RowFullWidth $align='center' id="summary-row-fees">
            <FeesBreakdown serviceFee={serviceFee} currencyFormat={currencyFormat} />
          </Styles.RowFullWidth>
        )}
        <Styles.RowFullWidth $align='space-between' id="summary-row-grand-total">
          <Styles.TextInfo $weight={700} size={16} id="summary-grand-total-label">
            {translate(['SUMMARY_FEES', 'TOTAL'])}
          </Styles.TextInfo>
          <Styles.TextInfo $weight={700} size={16} id="summary-grand-total-amount">
            {formatNumericValue({
              value : total,
              discount : 0,
              currencyFormat
            })}
          </Styles.TextInfo>
        </Styles.RowFullWidth>
        <Styles.RowFullWidth $align='center' id="summary-row-checkout-button">
          <BigButton
            disabled={isDisabled}
            onClick={() => {
              handleNextStep();
            }}
            id="summary-checkout-button"
          >
            {translate(['STEPS', 'TXT15'])}
          </BigButton>
        </Styles.RowFullWidth>
      </Styles.SummaryFooter>
    </Styles.Container>
  );
};

export default Summary;
